import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import AppShell from "../../../components/layout/AppShell";
import FileUpload from "../../../components/ui/FileUpload";
import { createInvoice, uploadInvoiceDocument, listSenders } from "../../../services/api";
import { useSelector } from "react-redux";

const CreateInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useSelector((s) => s.auth.userId);

  // Sender pre-filled when navigating from the Sender Detail page
  const prefillSender = location.state?.prefillSender || null;

  const [senders, setSenders] = useState([]);
  const [sendersLoading, setSendersLoading] = useState(true);

  const [form, setForm] = useState({
    amount:    "",
    senderId:  prefillSender?.id || "",
    invoiceId: "",   // maps to API invoiceId  (UI label: Reference Number)
    dueDate:   "",   // maps to API dueDate    (required)
    comment:   "",   // maps to API comment    (UI label: Description, optional)
  });
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listSenders()
      .then((res) => setSenders(res.data || []))
      .catch(() => setSenders([]))
      .finally(() => setSendersLoading(false));
  }, []);

  const set = (field, val) => {
    setForm((p) => ({ ...p, [field]: val }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0)
      errs.amount = "Enter a valid amount.";
    if (!form.senderId)
      errs.senderId = "Please select a sender.";
    if (!form.invoiceId.trim())
      errs.invoiceId = "Reference number is required.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Step 1: upload document if provided
      let docUrl = null;
      if (invoiceFile) {
        const uploadRes = await uploadInvoiceDocument(invoiceFile);
        docUrl = uploadRes?.data?.fileUrl;
        if (!docUrl) throw new Error("Document upload failed — no URL returned.");
      }

      // Step 2: create the payment request
      const res = await createInvoice({
        fromUserId: userId,
        toUserId:   form.senderId,
        currency:   "USD",
        amount:     Number(form.amount),
        invoiceId:  form.invoiceId,
        ...(form.dueDate  && { dueDate: form.dueDate }),
        ...(docUrl        && { docUrl }),
        ...(form.comment  && { comment: form.comment }),
      });

      toast.success("Invoice created successfully.");
      navigate(`/receiver/invoices/${res.data?.id}`);
    } catch (err) {
      toast.error(err?.message || "Failed to create invoice. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-[560px]">
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h1 className="text-xl font-bold text-gray-900 mb-1">Create Invoice</h1>
          <p className="text-sm text-gray-400 mb-6">
            Send a payment request to one of your registered senders.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-5">

              {/* Sender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Sender <span className="text-red-400">*</span>
                </label>
                {prefillSender ? (
                  // Locked display when sender is pre-filled from detail page
                  <div className="flex items-center justify-between w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{prefillSender.name}</p>
                      {prefillSender.email && (
                        <p className="text-xs text-gray-400 mt-0.5">{prefillSender.email}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="text-xs text-brand hover:underline"
                    >
                      Change
                    </button>
                  </div>
                ) : sendersLoading ? (
                  <div className="h-11 bg-gray-100 rounded-xl animate-pulse" />
                ) : (
                  <select
                    value={form.senderId}
                    onChange={(e) => set("senderId", e.target.value)}
                    className={`w-full border rounded-xl px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand ${
                      errors.senderId
                        ? "border-red-400 text-gray-400"
                        : form.senderId
                        ? "border-gray-200 text-gray-900"
                        : "border-gray-200 text-gray-400"
                    }`}
                  >
                    <option value="">Select sender...</option>
                    {senders.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} {s.email ? `(${s.email})` : ""}
                      </option>
                    ))}
                  </select>
                )}
                {!prefillSender && !sendersLoading && senders.length === 0 && (
                  <p className="mt-1 text-xs text-orange-500">
                    No senders linked yet.{" "}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => navigate("/receiver/senders/create")}
                    >
                      Add a sender
                    </button>{" "}
                    first.
                  </p>
                )}
                {errors.senderId && (
                  <p className="mt-1 text-xs text-red-500">{errors.senderId}</p>
                )}
              </div>

              {/* USD Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  USD Amount <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val !== "" && !/^\d*\.?\d{0,2}$/.test(val)) return;
                      set("amount", val);
                    }}
                    placeholder="0.00"
                    className={`w-full border rounded-xl pl-8 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand ${
                      errors.amount ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                </div>
                {errors.amount && (
                  <p className="mt-1 text-xs text-red-500">{errors.amount}</p>
                )}
              </div>

              {/* Reference Number → invoiceId */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Reference Number <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.invoiceId}
                  onChange={(e) => set("invoiceId", e.target.value)}
                  placeholder="e.g. INV-2025-001"
                  className={`w-full border rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand ${
                    errors.invoiceId ? "border-red-400" : "border-gray-200"
                  }`}
                />
                {errors.invoiceId && (
                  <p className="mt-1 text-xs text-red-500">{errors.invoiceId}</p>
                )}
              </div>

              {/* Due Date — required */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Due Date{" "}
                  <span className="text-xs text-gray-400 font-normal">Optional</span>
                </label>
                <input
                  type="date"
                  value={form.dueDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => set("dueDate", e.target.value)}
                  className={`w-full border rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand ${
                    errors.dueDate ? "border-red-400" : "border-gray-200"
                  }`}
                />
                {errors.dueDate && (
                  <p className="mt-1 text-xs text-red-500">{errors.dueDate}</p>
                )}
              </div>

              {/* Description → comment (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Description{" "}
                  <span className="text-xs text-gray-400 font-normal">Optional</span>
                </label>
                <textarea
                  value={form.comment}
                  onChange={(e) => set("comment", e.target.value)}
                  placeholder="Brief description of goods or services..."
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none"
                />
              </div>

              {/* Invoice Document (optional) */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <label className="text-sm font-medium text-gray-700">Invoice Document</label>
                  <span className="text-xs text-gray-400">Optional — PDF, PNG, JPG</span>
                </div>
                <FileUpload
                  name="invoiceDoc"
                  accept={["application/pdf", "image/png", "image/jpeg"]}
                  maxSizeMB={10}
                  hint="Drag & drop or click to upload"
                  onChange={(file) => {
                    setInvoiceFile(file);
                    setErrors((p) => ({ ...p, invoiceFile: "" }));
                  }}
                />
              </div>
            </div>

            <div className="mt-8 flex justify-center">
              <button
                type="submit"
                disabled={submitting}
                className="bg-brand text-white text-sm font-semibold px-16 py-3 rounded-lg hover:bg-brandHover transition-colors disabled:opacity-60"
              >
                {submitting ? "Creating…" : "Create Invoice"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppShell>
  );
};

export default CreateInvoice;
